/**
 * Range-mode controls, deliberately two independent components.
 *
 * They are gated differently: the metric selector only makes sense when week
 * columns actually render, but the inactivity filter applies to ANY range —
 * including one that collapsed to a single week bucket. Gating both on
 * "week mode" made the filter silently disappear for a valid range whose UI
 * and docs promised it (PR #5 review, second pass).
 */

import React from 'react';
import { FUNNEL_METRIC_OPTIONS, FunnelMetricKey } from '../../../../types/salesTeamFunnel';

/** Which metric the week columns show. Render only when week columns exist. */
export const FunnelMetricSelect: React.FC<{
  value: FunnelMetricKey;
  onChange: (next: FunnelMetricKey) => void;
}> = ({ value, onChange }) => (
  <div className="flex items-center gap-2">
    <label htmlFor="funnel-week-metric" className="text-sm font-medium text-gray-700">
      Weekly columns show
    </label>
    <select
      id="funnel-week-metric"
      value={value}
      onChange={(e) => onChange(e.target.value as FunnelMetricKey)}
      className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm py-1"
    >
      {FUNNEL_METRIC_OPTIONS.map((o) => (
        <option key={o.key} value={o.key}>{o.label}</option>
      ))}
    </select>
  </div>
);

/** Display-only row filter. Render for every range, week columns or not. */
export const FunnelInactiveFilter: React.FC<{
  checked: boolean;
  onChange: (next: boolean) => void;
}> = ({ checked, onChange }) => (
  <label className="flex items-center gap-2 text-sm text-gray-600">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
    />
    Hide salespeople with no activity in this range
  </label>
);

/** Control bar wrapper — keeps the print:hidden and hint copy in one place. */
export const FunnelRangeControls: React.FC<{
  showMetricSelect: boolean;
  metric: FunnelMetricKey;
  onMetricChange: (next: FunnelMetricKey) => void;
  hideInactive: boolean;
  onHideInactiveChange: (next: boolean) => void;
  /** Number of week columns, used for the scroll hint. */
  periodCount: number;
}> = ({
  showMetricSelect, metric, onMetricChange, hideInactive, onHideInactiveChange, periodCount,
}) => (
  <div className="mb-3 print:hidden">
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
      {showMetricSelect && <FunnelMetricSelect value={metric} onChange={onMetricChange} />}
      <FunnelInactiveFilter checked={hideInactive} onChange={onHideInactiveChange} />
      <span className="text-xs text-gray-500">
        {showMetricSelect
          ? 'Range Total always shows all four metrics. Subtotals never change.'
          : 'Hiding rows never changes subtotals or totals.'}
      </span>
    </div>

    {/* The table is deliberately wider than the modal; say so, because a
        cut-off table with an overlay scrollbar reads as broken. */}
    {showMetricSelect && (
      <p className="mt-2 text-xs text-gray-500">
        <span aria-hidden="true">↔</span> {periodCount} week columns — wider than the window.
        Use the arrow buttons by each location, drag the scrollbar under the table, or
        Shift + mouse wheel. The Team / Salesperson column stays pinned while you scroll.
      </p>
    )}
  </div>
);
